/**
 * Wire-format schemas for `<site>/wp-json/ezqr/v1/site` — the per-site
 * capability endpoint. Parses snake_case JSON exactly as the backend
 * sends it and transforms to the camelCase entity in
 * `src/entities/site-capabilities.ts`.
 *
 * Quirks handled here:
 *  - `_apiVersion` (leading underscore) → `apiVersion`
 *  - `endpoints` arrives as `[]` (empty) OR `{ k: path }` — normalised
 *    to a record before the entity sees it.
 *  - A feature key may be ABSENT for sites where the plugin isn't
 *    provisioned (passes through as undefined; consumers handle
 *    absence vs. `enabled: false` distinctly).
 *  - `site.logo` ships in TWO shapes (`string | null` historically,
 *    `{ id, url, w, h } | null` going forward). Accept both, normalise
 *    to the URL string the entity / SiteLogo component expects.
 */
import { z } from 'zod'

import {
  type DualPricingConfig,
  type Features,
  type OnlineOrderingConfig,
  type OrderingDualPricing,
  type SiteCapabilities,
  type SiteIdentity,
} from '@/entities/site-capabilities'

/**
 * Normalise the `endpoints` field at the wire boundary: backend uses
 * `[]` (empty array) for "no endpoints" and `{ name: path }` for the
 * non-empty case. We collapse the empty-array form to `{}` so every
 * consumer can treat it as a record without a type-guard.
 */
const endpointsWire = z.preprocess(
  (v) => (Array.isArray(v) ? {} : v),
  z.record(z.string(), z.string()),
)

function featureEnvelopeWire<C extends z.ZodTypeAny, Out>(
  configWire: C,
  toEntityConfig: (raw: z.infer<C>) => Out,
) {
  return z
    .object({
      enabled: z.boolean(),
      visibility: z.string(),
      reason: z.string().nullable(),
      config: configWire,
      endpoints: endpointsWire,
    })
    .transform((f) => ({
      enabled: f.enabled,
      visibility: f.visibility,
      reason: f.reason,
      config: toEntityConfig(f.config),
      endpoints: f.endpoints,
    }))
}

// ---- site identity wire ------------------------------------------------

/**
 * Backend has shipped `site.logo` in two shapes:
 *  - originally `string | null` (the raw URL)
 *  - now `{ id, url, w, h } | null` (an attachment object with
 *    dimensions, useful for `srcset` later)
 *
 * Accept both so the SPA survives backend upgrades / downgrades, and
 * normalise to a single URL string at the entity boundary — the
 * SiteLogo component still wants just a `string | null`. We discard
 * the dimensions for now; revisit if we ever start emitting `srcset`.
 */
const SiteLogoWire = z
  .union([z.string().url(), z.object({ url: z.string().url() }).passthrough(), z.null()])
  .transform((v): string | null => {
    if (v === null) return null
    if (typeof v === 'string') return v
    return v.url
  })

const SiteIdentityWire = z
  .object({
    id: z.number().int().positive(),
    name: z.string(),
    url: z.string().url(),
    base_url: z.string().url(),
    logo: SiteLogoWire,
    locale: z.string(),
    theme: z.object({ slug: z.string() }),
  })
  .transform(
    (s): SiteIdentity => ({
      id: s.id,
      name: s.name,
      url: s.url,
      baseUrl: s.base_url,
      logo: s.logo,
      locale: s.locale,
      theme: { slug: s.theme.slug },
    }),
  )

// ---- dual pricing config wire ------------------------------------------

const DualPricingConfigWire = z.object({
  base_price_type: z.string(),
  fee_percent: z.number(),
  fee_direction: z.string(),
})

const dualPricingToEntity = (c: z.infer<typeof DualPricingConfigWire>): DualPricingConfig => ({
  basePriceType: c.base_price_type,
  feePercent: c.fee_percent,
  feeDirection: c.fee_direction,
})

const DualPricingFeatureWire = featureEnvelopeWire(DualPricingConfigWire, dualPricingToEntity)

// ---- online ordering config wire ---------------------------------------

const OrderingDualPricingWire = z.object({
  enabled: z.boolean(),
  base_type: z.string(),
  fee_pct: z.number(),
})

const orderingDualToEntity = (c: z.infer<typeof OrderingDualPricingWire>): OrderingDualPricing => ({
  enabled: c.enabled,
  baseType: c.base_type,
  feePct: c.fee_pct,
})

const OnlineOrderingConfigWire = z.object({
  merchant_id: z.string(),
  business_name: z.string(),
  flow: z.string(),
  dualpricing: OrderingDualPricingWire,
  catalog_mode: z.boolean(),
})

const onlineOrderingToEntity = (
  c: z.infer<typeof OnlineOrderingConfigWire>,
): OnlineOrderingConfig => ({
  merchantId: c.merchant_id,
  businessName: c.business_name,
  flow: c.flow,
  dualpricing: orderingDualToEntity(c.dualpricing),
  catalogMode: c.catalog_mode,
})

const OnlineOrderingFeatureWire = featureEnvelopeWire(
  OnlineOrderingConfigWire,
  onlineOrderingToEntity,
)

// ---- features bag ------------------------------------------------------

/**
 * Generic envelope for unknown future features — keeps endpoints
 * normalised and config opaque so the SPA can still surface them
 * generically (e.g. an "Unknown feature: foo (enabled)" debug row).
 */
const UnknownFeatureWire = z
  .object({
    enabled: z.boolean(),
    visibility: z.string(),
    reason: z.string().nullable(),
    config: z.unknown(),
    endpoints: endpointsWire,
  })
  .transform((f) => ({
    enabled: f.enabled,
    visibility: f.visibility,
    reason: f.reason,
    config: f.config,
    endpoints: f.endpoints,
  }))

const FeaturesWire = z
  .object({
    online_ordering: OnlineOrderingFeatureWire.optional(),
    dual_pricing: DualPricingFeatureWire.optional(),
  })
  .catchall(UnknownFeatureWire)
  .transform((f): Features => f as unknown as Features)

// ---- top-level ---------------------------------------------------------

export const siteCapabilitiesWire = z
  .object({
    _apiVersion: z.literal(1),
    site: SiteIdentityWire,
    features: FeaturesWire,
  })
  .transform(
    (s): SiteCapabilities => ({
      apiVersion: s._apiVersion,
      site: s.site,
      features: s.features,
    }),
  )
